import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { isEmpty } from 'lodash';
import styled from '@emotion/styled';

import {
    Button,
    ButtonGroup,
    ButtonGroupTestID,
    HeadingText50 as StepTitle,
    pxToRem,
} from '@modernhealth/vital-components';

import { colors as dsColors } from '@modernhealth/vital-foundation';

import { font, mediaQueries } from 'src/shared/styles/variables';
import { SingleSelectQuestion } from 'src/modules/assessments/components/SingleSelectQuestion';
import { navigationService } from 'src/config/rootService';
import {
    CareNavigationRequestContactPreference,
    CareNavigationRequestSource,
} from 'src/generated/webapp_gql';

import { CareNavigationSteps } from '../components/CareNavigationSteps';
import { CareNavigationRequest } from '../models/benefits';
import { useIsCareNavEnabledUser } from '../hooks/useIsCareNavEnabledUser';
import { CareNavigationContactMethodStepTestId } from '../models/testsIds';

const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: pxToRem(500),
    marginTop: pxToRem(100),
    marginRight: 'auto',
    marginLeft: 'auto',
    [mediaQueries.sm]: {
        padding: '0.5rem',
    },
});

const ButtonGroupContainer = styled.div({
    marginTop: pxToRem(32),
    marginBottom: pxToRem(24),
});

const TitleContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: pxToRem(24),
    marginTop: pxToRem(32),
});

const FormContainer = styled.div({
    textAlign: 'center',
    width: '100%',
    [mediaQueries.sm]: {
        paddingBottom: 0,
    },
});

const SelectableTitle = styled.p({
    textAlign: 'center',
    marginTop: 10,
});

const SelectableSubtitle = styled.p({
    textAlign: 'center',
    lineHeight: '22px',
});

const SelectableContainer = styled('ul')({
    width: '100%',
    listStyleType: 'none',
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 0,
    [mediaQueries.md]: {
        flexDirection: 'column',
    },
});

interface SelectableProps {
    selected: boolean;
}

const Selectable = styled.li<SelectableProps>(props => {
    const currentFont = props.selected
        ? font.heavy(font.size.medium)
        : font.roman(font.size.medium);
    return {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        backgroundColor: dsColors.white,
        color: dsColors.gray85,
        padding: '19px 1rem',
        border: `2px solid ${props.selected ? dsColors.blue55 : dsColors.gray10}`,
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        userSelect: 'none',
        borderRadius: 6,
        marginBottom: 10,
        minHeight: 64,
        width: '100%',
        ...currentFont,
    };
});

export const CareNavigationContactMethodStep: React.FC = () => {
    const location = useLocation<CareNavigationRequest>();
    const { isUserEnabledFlow } = useIsCareNavEnabledUser();

    if (!isUserEnabledFlow) {
        navigationService.navigate('/dashboard');
    }

    if (isEmpty(location.state)) {
        navigationService.navigate('/care-navigation-request');
    }

    const { t } = useTranslation();
    const [requestState, setRequestState] = useState<CareNavigationRequest>(location.state);

    const options = [
        {
            title: 'benefits:care_navigation_contact_method_step.answer_one_title',
            subTitle: 'benefits:care_navigation_contact_method_step.answer_one_subtitle',
            betaSubtitle: 'benefits:care_navigation_contact_method_step.answer_one_beta_subtitle',
            value: CareNavigationRequestContactPreference.Email,
        },
        {
            title: 'benefits:care_navigation_contact_method_step.answer_two_title',
            subTitle: 'benefits:care_navigation_contact_method_step.answer_two_subtitle',
            betaSubtitle: 'benefits:care_navigation_contact_method_step.answer_two_beta_subtitle',
            value: CareNavigationRequestContactPreference.Phone,
        },
    ];

    const isContinueButtonDisabled = () => {
        return !(requestState.contactPreference !== '');
    };

    const handleContinue = () => {
        navigationService.navigate('/care-navigation-request-contact', requestState);
    };

    const handleBack = () => {
        if (requestState.source === CareNavigationRequestSource.WellbeingReport) {
            navigationService.navigate('/wellbeing-report');
        } else {
            navigationService.navigate('/care-navigation-details', requestState);
        }
    };

    return (
        <Wrapper>
            {requestState.source !== CareNavigationRequestSource.WellbeingReport && (
                <CareNavigationSteps step={3} />
            )}

            <TitleContainer>
                <StepTitle centered>
                    {t('benefits:care_navigation_contact_method_step.question')}
                </StepTitle>
            </TitleContainer>

            <FormContainer>
                <SingleSelectQuestion
                    currentRate={requestState.contactPreference as string}
                    onRateChange={value =>
                        setRequestState({
                            ...requestState,
                            contactPreference: value as CareNavigationRequestContactPreference,
                        })
                    }
                    options={[]}
                />

                <SelectableContainer>
                    {options.map(option => (
                        <Selectable
                            key={option.value}
                            onClick={() =>
                                setRequestState({
                                    ...requestState,
                                    contactPreference:
                                        option.value as CareNavigationRequestContactPreference,
                                })
                            }
                            selected={requestState.contactPreference === option.value}
                            data-testid={`${CareNavigationContactMethodStepTestId.SelectableOption}${option.value}`}
                        >
                            <SelectableTitle>{t(option.title)}</SelectableTitle>
                            <SelectableSubtitle>
                                {t(
                                    requestState.source === CareNavigationRequestSource.ContactUs
                                        ? option.subTitle
                                        : option.betaSubtitle,
                                )}
                            </SelectableSubtitle>
                        </Selectable>
                    ))}
                </SelectableContainer>
                <ButtonGroupContainer>
                    <ButtonGroup>
                        <Button
                            variant="secondary"
                            onClick={handleBack}
                            data-testid={ButtonGroupTestID.FirstButton}
                        >
                            {t('common.back')}
                        </Button>
                        <Button
                            variant="primary"
                            size="large"
                            onClick={handleContinue}
                            disabled={isContinueButtonDisabled()}
                            data-testid={ButtonGroupTestID.SecondButton}
                        >
                            {t('common.continue')}
                        </Button>
                    </ButtonGroup>
                </ButtonGroupContainer>
            </FormContainer>
        </Wrapper>
    );
};
